import { registerRelationshipPack, registerShape } from '../../../lib/diagram/registries';
import { registerDomainProfile } from '../../../core/data-profiles';
import { registerDomainPresets } from '../../../core/assistant/stores/viewRulesStore';
import { registerFolderCoverageProvider } from '../../../core/folderCoverage/registry';
import { compassFolderCoverageProvider } from '../../compass/services/folderCoverage/FolderCoverageService';
import { NetworkProfile } from '../NetworkProfile';
import { NetworkRelationshipPack } from '../connectors/relationships.pack';
import { registerNetworkConnectors } from '../connectors/manifests';
import { registerNetworkFieldPacks } from '../fields';
import { registerNetworkOntology } from '../ontology';
import { registerNetworkProfiles } from '../profiles';
import { NETWORK_VIEW_PRESETS } from '../presets/networkPresets';
import { AwsShapeManifests } from './aws-shapes.manifest';
import { BasicShapeManifests } from './basic-shapes.manifest';
import { CompassBubbleManifest } from './compass-bubble.manifest';
import { CompassEnvLabelManifest } from './compass-env-label.manifest';
import { ProviderShapeManifests } from './provider-shapes.manifest';

export function registerNetworkDomain() {
  // 1. Register semantic ontology types FIRST so other registration steps can query isA().
  registerNetworkOntology();

  // 2. Domain data profile
  registerDomainProfile(NetworkProfile);

  // 3. Relationship pack (edge categories + subtypes)
  registerRelationshipPack(NetworkRelationshipPack);

  // 4. Connector manifests (visual edge rendering)
  registerNetworkConnectors();

  // 5. Field packs (data schema)
  registerNetworkFieldPacks();

  // 6. View profiles (topology, security, cost)
  registerNetworkProfiles();

  // 7. AWS shape manifests (provider-specific visuals over semantic types)
  for (const manifest of AwsShapeManifests) {
    registerShape('network', manifest);
  }

  // 8. Provider dialect shape manifests (Azure, Google Cloud, Kubernetes,
  //    and provider-neutral cloud). They reuse the same semantic ontology
  //    leaves as AWS so analysis and assistant tools can query across
  //    provider visuals.
  for (const manifest of ProviderShapeManifests) {
    registerShape('network', manifest);
  }

  // 9. Basic network shape manifests (provider-neutral: router, switch,
  //    firewall, server, database, laptop, cloud, internet, user). These
  //    register the same semanticType as the cloud-specific variants so
  //    ontology queries like isA(node, 'datastore') match both a basic
  //    `basic_database` and an `aws_rds_postgres`.
  for (const manifest of BasicShapeManifests) {
    registerShape('network', manifest);
  }

  // 10. Compass Explorer synthetic shape — `compass_bubble` renders at
  //    overview zoom levels (L1/L2) to represent accounts/VPCs/subnets
  //    with resource counts. Registered under the `network` diagram
  //    type so the explorer page (which uses diagramType='network')
  //    picks it up alongside the AWS manifests.
  registerShape('network', CompassBubbleManifest);
  registerShape('network', CompassEnvLabelManifest);

  // 11. View presets (Topology/Security/Compute/Data/IAM). These live
  //     in the shared viewRulesStore and are gated by `domain: 'network'`
  //     so they only surface on network diagrams.
  registerDomainPresets(NETWORK_VIEW_PRESETS);

  // 12. Folder coverage provider — drives the dashboard's Sources
  //     section HUD for network folders (which include Compass
  //     Explorer folders — they share the `network` domain value).
  registerFolderCoverageProvider(compassFolderCoverageProvider);
}
